import { Component, Input, Output, EventEmitter } from '@angular/core';

/**
 * Configuration options for the Feature Flags Admin component
 */
export interface FeatureFlagsAdminConfig {
  /**
   * Theme for the admin interface
   * @default 'default'
   */
  theme?: 'default' | 'dark' | 'light';

  /**
   * Whether to show the feature flag evaluation testing panel
   * @default true
   */
  showEvaluation?: boolean;

  /**
   * Whether to include advanced rule building capabilities
   * @default true
   */
  advancedRuleBuilder?: boolean;

  /**
   * Default filter status for the feature flags list
   * @default 'active'
   */
  defaultFilterStatus?: 'active' | 'archived' | 'all';
}

/**
 * Main component for the Feature Flags Admin interface
 * Provides a comprehensive UI for managing feature flags
 */
@Component({
  selector: 'cbre-feature-flags-admin',
  templateUrl: './feature-flags-admin.component.html',
  styleUrls: ['./feature-flags-admin.component.scss'],
  standalone: false,
})
export class FeatureFlagsAdminComponent {
  /**
   * Configuration options for the component
   */
  @Input() config: FeatureFlagsAdminConfig = {
    theme: 'default',
    showEvaluation: true,
    advancedRuleBuilder: true,
    defaultFilterStatus: 'active',
  };

  /**
   * Event emitted when a feature flag is created
   */
  @Output() flagCreated = new EventEmitter<string>();

  /**
   * Event emitted when a feature flag is updated
   */
  @Output() flagUpdated = new EventEmitter<string>();

  /**
   * Event emitted when a feature flag is archived
   */
  @Output() flagArchived = new EventEmitter<string>();

  /**
   * Currently active view
   */
  activeView: 'list' | 'detail' | 'create' | 'edit' = 'list';

  /**
   * ID of the currently selected feature flag
   */
  selectedFlagId: string;

  /**
   * Navigate to the feature flags list
   */
  onShowList(): void {
    this.activeView = 'list';
    this.selectedFlagId = null;
  }

  /**
   * Navigate to the create feature flag form
   */
  onCreateFlag(): void {
    this.activeView = 'create';
  }

  /**
   * Navigate to the flag details page
   */
  onShowFlag(id: string): void {
    this.activeView = 'detail';
    this.selectedFlagId = id;
  }

  /**
   * Navigate to the edit flag page
   */
  onEditFlag(id: string): void {
    this.activeView = 'edit';
    this.selectedFlagId = id;
  }

  /**
   * Handle flag creation success
   */
  onFlagCreatedSuccess(id: string): void {
    this.flagCreated.emit(id);
    this.activeView = 'detail';
    this.selectedFlagId = id;
  }

  /**
   * Handle flag update success
   */
  onFlagUpdatedSuccess(id: string): void {
    this.flagUpdated.emit(id);
    this.activeView = 'detail';
    this.selectedFlagId = id;
  }

  /**
   * Handle flag archive success
   */
  onFlagArchivedSuccess(id: string): void {
    this.flagArchived.emit(id);
    this.activeView = 'list';
    this.selectedFlagId = null;
  }
}
